import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import WarningIcon from '@material-ui/icons/Warning';
import { InfoCard } from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import {
  useChaosExperiment,
  ChaosExperimentType,
  ChaosStatus,
  ChaosConfig,
  PodChaosConfig,
  NetworkChaosConfig,
  IOChaosConfig,
} from '../hooks/useChaosExperiment';

const useStyles = makeStyles(theme => ({
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 600,
    fontSize: 12,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  historyItem: {
    display: 'flex',
    gap: theme.spacing(2),
    fontSize: 13,
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  warningIcon: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
}));

const STATUS_COLORS: Record<ChaosStatus, 'default' | 'primary' | 'secondary'> = {
  idle: 'default',
  running: 'primary',
  completed: 'default',
  failed: 'secondary',
  paused: 'default',
  unknown: 'default',
};

const STATUS_LABELS: Record<ChaosStatus, string> = {
  idle: 'Idle',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  paused: 'Paused',
  unknown: 'Unknown',
};

interface ChaosExperimentPanelProps {
  entity: Entity;
}

export const ChaosExperimentPanel = ({ entity }: ChaosExperimentPanelProps) => {
  const classes = useStyles();
  const { experiments, loading, trigger, kill } = useChaosExperiment(entity);
  const [expanded, setExpanded] = useState(true);
  const [experimentType, setExperimentType] = useState<ChaosExperimentType>('PodChaos');
  const [duration, setDuration] = useState('30s');
  const [triggering, setTriggering] = useState(false);
  const [killing, setKilling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [killConfirmOpen, setKillConfirmOpen] = useState(false);

  // PodChaos config
  const [podAction, setPodAction] = useState<PodChaosConfig['action']>('pod-kill');

  // NetworkChaos config
  const [networkAction, setNetworkAction] = useState<NetworkChaosConfig['action']>('delay');
  const [networkDelay, setNetworkDelay] = useState('100ms');
  const [networkLoss, setNetworkLoss] = useState('25');

  // StressChaos config
  const [cpuWorkers, setCpuWorkers] = useState(1);
  const [memorySize, setMemorySize] = useState('256MB');

  // IOChaos config
  const [ioAction, setIoAction] = useState<IOChaosConfig['action']>('delay');
  const [ioDelay, setIoDelay] = useState('100ms');

  const isRunning = experiments.some(e => e.status === 'running');
  const latestStatus = experiments[0]?.status ?? 'idle';

  const buildConfig = useCallback((): ChaosConfig => {
    switch (experimentType) {
      case 'PodChaos':
        return { action: podAction, duration };
      case 'NetworkChaos':
        return { action: networkAction, delay: networkDelay, loss: networkLoss, duration };
      case 'StressChaos':
        return { cpuWorkers, memorySize, duration };
      case 'IOChaos':
        return { action: ioAction, delay: ioDelay, duration };
      default:
        return { action: 'pod-kill', duration } as PodChaosConfig;
    }
  }, [experimentType, podAction, networkAction, networkDelay, networkLoss, cpuWorkers, memorySize, ioAction, ioDelay, duration]);

  const handleRunClick = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmRun = useCallback(async () => {
    setConfirmOpen(false);
    setTriggering(true);
    try {
      await trigger(experimentType, buildConfig());
    } finally {
      setTriggering(false);
    }
  }, [trigger, experimentType, buildConfig]);

  const handleKill = useCallback(async () => {
    const runningExp = experiments.find(e => e.status === 'running');
    if (!runningExp) return;
    setKillConfirmOpen(false);
    setKilling(true);
    try {
      await kill(runningExp.name, runningExp.kind);
    } finally {
      setKilling(false);
    }
  }, [kill, experiments]);

  return (
    <Box>
      <Box className={classes.sectionHeader} onClick={() => setExpanded(!expanded)}>
        <Typography style={{ fontWeight: 600, fontSize: '1rem', padding: '8px 0', marginTop: 24 }}>
          🔥 Chaos Engineering
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <InfoCard title="Chaos Mesh Experiment" noPadding>
          <Box p={2}>
            <Grid container spacing={2} alignItems="center">
              {/* Experiment Type */}
              <Grid item xs={12} sm={3}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={experimentType}
                    onChange={e => setExperimentType(e.target.value as ChaosExperimentType)}
                    label="Type"
                    disabled={isRunning}
                  >
                    <MenuItem value="PodChaos">PodChaos</MenuItem>
                    <MenuItem value="NetworkChaos">NetworkChaos</MenuItem>
                    <MenuItem value="StressChaos">StressChaos</MenuItem>
                    <MenuItem value="IOChaos">IOChaos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Type-specific config */}
              {experimentType === 'PodChaos' && (
                <Grid item xs={12} sm={3}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={podAction}
                      onChange={e => setPodAction(e.target.value as PodChaosConfig['action'])}
                      label="Action"
                      disabled={isRunning}
                    >
                      <MenuItem value="pod-kill">Pod Kill</MenuItem>
                      <MenuItem value="pod-failure">Pod Failure</MenuItem>
                      <MenuItem value="container-kill">Container Kill</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {experimentType === 'NetworkChaos' && (
                <>
                  <Grid item xs={6} sm={2}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel>Action</InputLabel>
                      <Select
                        value={networkAction}
                        onChange={e => setNetworkAction(e.target.value as NetworkChaosConfig['action'])}
                        label="Action"
                        disabled={isRunning}
                      >
                        <MenuItem value="delay">Delay</MenuItem>
                        <MenuItem value="loss">Packet Loss</MenuItem>
                        <MenuItem value="partition">Partition</MenuItem>
                        <MenuItem value="bandwidth">Bandwidth</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {networkAction === 'delay' && (
                    <Grid item xs={6} sm={2}>
                      <TextField label="Latency" variant="outlined" size="small" value={networkDelay} onChange={e => setNetworkDelay(e.target.value)} disabled={isRunning} fullWidth />
                    </Grid>
                  )}
                  {networkAction === 'loss' && (
                    <Grid item xs={6} sm={2}>
                      <TextField label="Loss %" variant="outlined" size="small" value={networkLoss} onChange={e => setNetworkLoss(e.target.value)} disabled={isRunning} fullWidth />
                    </Grid>
                  )}
                </>
              )}

              {experimentType === 'StressChaos' && (
                <>
                  <Grid item xs={6} sm={2}>
                    <TextField label="CPU Workers" variant="outlined" size="small" type="number" value={cpuWorkers} onChange={e => setCpuWorkers(Number(e.target.value))} disabled={isRunning} fullWidth />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField label="Memory" variant="outlined" size="small" value={memorySize} onChange={e => setMemorySize(e.target.value)} disabled={isRunning} fullWidth />
                  </Grid>
                </>
              )}

              {experimentType === 'IOChaos' && (
                <>
                  <Grid item xs={6} sm={2}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel>Action</InputLabel>
                      <Select
                        value={ioAction}
                        onChange={e => setIoAction(e.target.value as IOChaosConfig['action'])}
                        label="Action"
                        disabled={isRunning}
                      >
                        <MenuItem value="delay">Delay</MenuItem>
                        <MenuItem value="fault">Fault</MenuItem>
                        <MenuItem value="attrOverride">Attr Override</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {ioAction === 'delay' && (
                    <Grid item xs={6} sm={2}>
                      <TextField label="Delay" variant="outlined" size="small" value={ioDelay} onChange={e => setIoDelay(e.target.value)} disabled={isRunning} fullWidth />
                    </Grid>
                  )}
                </>
              )}

              {/* Duration */}
              <Grid item xs={6} sm={2}>
                <TextField label="Duration" variant="outlined" size="small" value={duration} onChange={e => setDuration(e.target.value)} disabled={isRunning} fullWidth />
              </Grid>

              {/* Status + Run */}
              <Grid item xs={3} sm={1}>
                <Chip
                  className={classes.statusChip}
                  label={STATUS_LABELS[latestStatus]}
                  color={STATUS_COLORS[latestStatus]}
                  size="small"
                  variant={isRunning ? 'default' : 'outlined'}
                />
              </Grid>
              <Grid item xs={3} sm={1}>
                {isRunning ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<StopIcon />}
                    onClick={() => setKillConfirmOpen(true)}
                    disabled={killing || loading}
                    fullWidth
                    size="small"
                  >
                    {killing ? 'Stopping…' : 'Stop'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleRunClick}
                    disabled={triggering || loading || isRunning}
                    fullWidth
                    size="small"
                  >
                    Run
                  </Button>
                )}
              </Grid>
            </Grid>

            {/* Experiment History */}
            {experiments.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" style={{ fontWeight: 600 }}>
                  Recent Experiments
                </Typography>
                {experiments.slice(0, 5).map(exp => (
                  <Box key={exp.name} className={classes.historyItem}>
                    <span><strong>{exp.kind}</strong></span>
                    {exp.action && <span>{exp.action}</span>}
                    <span>{exp.name}</span>
                    <span>{STATUS_LABELS[exp.status]}</span>
                    <span>{exp.startTime ? new Date(exp.startTime).toLocaleString() : '—'}</span>
                    {exp.duration && <span>{exp.duration}</span>}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </InfoCard>
      </Collapse>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          <WarningIcon className={classes.warningIcon} />
          Confirm Chaos Experiment
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to run a <strong>{experimentType}</strong> experiment
            for <strong>{duration}</strong>. This will inject failures into your
            running pods and may cause service disruption.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleConfirmRun} color="secondary" variant="contained">
            Run Experiment
          </Button>
        </DialogActions>
      </Dialog>
      {/* Kill Confirmation Dialog */}
      <Dialog open={killConfirmOpen} onClose={() => setKillConfirmOpen(false)}>
        <DialogTitle>
          <WarningIcon className={classes.warningIcon} />
          Stop Chaos Experiment
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the running chaos experiment and stop all fault injection.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillConfirmOpen(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleKill} color="secondary" variant="contained">
            Stop &amp; Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
